import {
  CategoryUnionType,
  CompanyLinkType,
  useUpdateCompanyLink as useUpdateCompanyLinkApi,
} from '@api';
import { useNotification } from '@common';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { UpdateCompanyLinkTypeHandleConnectedArtists } from 'src/api/generated/touring/types';
import { CompanyLinkFormValuesType } from '../../types';
import {
  getShouldHandleConnectedArtists,
  openConnectedArtistsChoiceModal,
} from './helpers';

const TRANS_KEY = 'NOTIFICATIONS.EDIT_COMPANY_LINK';

export type CompanyLinkUpdateMutationArgsType = {
  companyLink: CompanyLinkType;
  data: CompanyLinkFormValuesType;
  onSuccessCallback?: () => void;
};

type UseUpdateCompanyLinkReturnType = {
  updateCompanyLink: (args: CompanyLinkUpdateMutationArgsType) => Promise<void>;
};

export const useUpdateCompanyLink = (): UseUpdateCompanyLinkReturnType => {
  const { mutateAsync } = useUpdateCompanyLinkApi();
  const { showError, showSuccess } = useNotification();
  const { t } = useTranslation();

  const updateCompanyLink = async ({
    companyLink,
    data,
    onSuccessCallback,
  }: CompanyLinkUpdateMutationArgsType) => {
    let handleConnectedArtists:
      | UpdateCompanyLinkTypeHandleConnectedArtists
      | undefined;

    const shouldHandleConnectedArtists = getShouldHandleConnectedArtists({
      hasArtistConnections: companyLink.hasArtistConnections,
      initialSiteId: companyLink.siteId,
      submittedSiteId: data.siteId,
    });

    if (shouldHandleConnectedArtists) {
      handleConnectedArtists = await openConnectedArtistsChoiceModal(t);
    }

    await mutateAsync(
      {
        companyLinkId: companyLink.linkId,
        data: {
          ...data,
          categories: data.categories as CategoryUnionType[],
          handleConnectedArtists,
        },
      },
      {
        onSuccess: () => {
          showSuccess({
            title: `${TRANS_KEY}.SUCCESS.TITLE`,
            message: `${TRANS_KEY}.SUCCESS.MESSAGE`,
          });
          onSuccessCallback?.();
        },
        onError: (error: AxiosError) => {
          showError({
            title: `${TRANS_KEY}.ERROR.TITLE`,
            message: `${TRANS_KEY}.ERROR.MESSAGE`,
            error,
          });
        },
      }
    );
  };

  return { updateCompanyLink };
};
